import { AnimatePresence } from 'framer-motion';
import { lazy, Suspense } from 'react';
import {
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
// hooks
import useGetGeneralAppData from '../hooks/useGetGeneralAppData';

// pages (lazy loaded for code splitting)
const Home = lazy(() => import('../pages/Home/Dashboards'));
const Triangulation = lazy(() => import('../pages/Triangulation'));
const Dashboard = lazy(() => import('../pages/Affreightments/Dashboard'));
const Lines = lazy(() => import('../pages/Affreightments/Lines'));
const EditLine = lazy(() => import('../pages/Affreightments/Lines/EditLine'));
const LineMap = lazy(() => import('../pages/Affreightments/Lines/Map'));
const NewLine = lazy(() => import('../pages/Affreightments/Lines/NewLine'));
const ShapePost = lazy(() => import('../pages/Affreightments/Lines/ShapePost'));
const Passengers = lazy(() => import('../pages/Affreightments/Passengers'));
const Vehicles = lazy(() => import('../pages/Affreightments/Vehicles'));
const EditVehicle = lazy(() => import('../pages/Affreightments/Vehicles/EditVehicle'));
const NewVehicle = lazy(() => import('../pages/Affreightments/Vehicles/NewVehicle'));
const AuditConsults = lazy(() => import('../pages/Audit/Consults'));
const AuditFiles = lazy(() => import('../pages/Audit/Files'));
const FileConsults = lazy(() => import('../pages/Audit/Files/Consults'));
const AutImplantation = lazy(() => import('../pages/AutImplantation'));
const ChangePassword = lazy(() => import('../pages/ChangePassword'));
const ClientUsers = lazy(() => import('../pages/ClientUsers'));
const EditClientUser = lazy(() => import('../pages/ClientUsers/EditInternalUser'));
const NewClientUser = lazy(() => import('../pages/ClientUsers/NewClientUser'));
const Company = lazy(() => import('../pages/Company'));
const Branches = lazy(() => import('../pages/Company/Branches'));
const NewBranch = lazy(() => import('../pages/Company/Branches/NewBranch'));
const FinancialList = lazy(() => import('../pages/Company/Financial'));
const Settings = lazy(() => import('../pages/Company/Settings'));
const ConsultResult = lazy(() => import('../pages/Consults/ConsultResult'));
const BiasTags = lazy(() => import('../pages/BiasTags'));
const FinancialForm = lazy(() => import('../pages/Company/Financial/Form'));
const FinancialInstallments = lazy(() => import('../pages/Company/Financial/Installments'));
const OmieInstallments = lazy(() => import('../pages/Financial/OmieInstallments'));
const ImportConsults = lazy(() => import('../pages/Consults/ImportConsults'));
const NewConsult = lazy(() => import('../pages/Consults/NewConsult'));
const SearchConsult = lazy(() => import('../pages/Consults/SearchConsult'));
const CustomersRequests = lazy(() => import('../pages/CustomersRequests'));
const ImportVtRegistrationDataDetails = lazy(() => import('../pages/ImportVtRegistrationData/Details'));
const ImportVtRegistrationDataList = lazy(() => import('../pages/ImportVtRegistrationData/List'));
const ImportVtRegistrationDataNew = lazy(() => import('../pages/ImportVtRegistrationData/New'));
const IagoLayout = lazy(() => import('../pages/Iago/Layout'));
const IagoCompanies = lazy(() => import('../pages/Iago/Companies'));
const IagoCompanyDetail = lazy(() => import('../pages/Iago/CompanyDetail'));
const IagoContactDetail = lazy(() => import('../pages/Iago/ContactDetail'));
const IagoContacts = lazy(() => import('../pages/Iago/Contacts'));
const IagoEscalated = lazy(() => import('../pages/Iago/Escalated'));
const IagoImports = lazy(() => import('../pages/Iago/Imports'));
const IagoLeads = lazy(() => import('../pages/Iago/Leads'));
const IagoOverview = lazy(() => import('../pages/Iago/Overview'));
const IagoParked = lazy(() => import('../pages/Iago/Parked'));
const Integration = lazy(() => import('../pages/Integration'));
const InternalUsers = lazy(() => import('../pages/InternalUsers'));
const EditInternalUser = lazy(() => import('../pages/InternalUsers/EditInternalUser'));
const NewInternalUser = lazy(() => import('../pages/InternalUsers/NewInternalUser'));
const ItensRelations = lazy(() => import('../pages/ItensRelations'));
const TransportLines = lazy(() => import('../pages/Lines'));
const LinesOperatorsBias = lazy(() => import('../pages/LinesOperatorsBias'));
const LinesOperatorsExceptions = lazy(() => import('../pages/LinesOperatorsExceptions'));
const NotFound = lazy(() => import('../pages/NotFound'));
const Operators = lazy(() => import('../pages/Operators'));
const Partners = lazy(() => import('../pages/Partners'));
const Integrator = lazy(() => import('../pages/Partners/Integrator'));
const Reclamations = lazy(() => import('../pages/Reclamations'));
const Consult = lazy(() => import('../pages/Reclamations/Consult'));
const ReclamationsDashboard = lazy(() => import('../pages/Reclamations/Dashboard'));
const ReclamationsAtPeriod = lazy(() => import('../pages/ReclamationsAtPeriod'));
const ReprocessConsults = lazy(() => import('../pages/ReprocessConsults'));
const AbateConsults = lazy(() => import('../pages/AbateConsults'));
const UnlockConsults = lazy(() => import('../pages/UnlockConsults'));
const UnlockingCompanies = lazy(() => import('../pages/UnlockingCompanies'));
const VtItens = lazy(() => import('../pages/VtItens'));
const Relations = lazy(() => import('../pages/VtItens/Relations'));
const Workplaces = lazy(() => import('../pages/Workplaces'));
const EditWorkplace = lazy(() => import('../pages/Workplaces/EditWorkplace'));
const NewWorkplace = lazy(() => import('../pages/Workplaces/NewWorkplace'));

export default function AllRoutes() {
  const location = useLocation();
  const { permissions, canAccessFinancial, canAccessIago } = useGetGeneralAppData();

  const companyPermissions = permissions.find((x) => x.codPag === 1);
  const companySettingsPermissions = permissions.find((x) => x.codPag === 2);
  const clientUsersPermissions = permissions.find((x) => x.codPag === 3);
  const workplacesPermissions = permissions.find((x) => x.codPag === 4);
  const autImplantationPermissions = permissions.find((x) => x.codPag === 5);
  const routingPermissions = permissions.find((x) => x.codPag === 6);
  const routingFilePermissions = permissions.find((x) => x.codPag === 7);
  const reclamationsPermissions = permissions.find((x) => x.codPag === 8);
  const affreightmentsDashboardPermissions = permissions.find((x) => x.codPag === 9);
  const affreightmentsLinesPermissions = permissions.find((x) => x.codPag === 10);
  const affreightmentsVehiclesPermissions = permissions.find((x) => x.codPag === 11);
  const affreightmentsPassengersPermissions = permissions.find((x) => x.codPag === 12);
  const triangulationPermissions = permissions.find((x) => x.codPag === 13);
  const routingFilesPermissions = permissions.find((x) => x.codPag === 15);
  const unlockCompaniesPermissions = permissions.find((x) => x.codPag === 16);
  const operatorsPermissions = permissions.find((x) => x.codPag === 17);
  const vtItensPermissions = permissions.find((x) => x.codPag === 18);
  const vtItensRelationsPermissions = permissions.find((x) => x.codPag === 19);
  const linesPermissions = permissions.find((x) => x.codPag === 20);
  const internalUsersPermissions = permissions.find((x) => x.codPag === 21);
  const partnersPermissions = permissions.find((x) => x.codPag === 22);

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<div />}>
        <Routes
          location={location}
          key={location.pathname}
        >
          <Route path="/" element={<Home activeSidebar="Home" />} />

          <Route path="/changePassword" element={<ChangePassword />} />

          {(clientUsersPermissions && clientUsersPermissions.vizualizar) && (
            <>
              <Route path="/clientUsers" element={<ClientUsers />} />
              {clientUsersPermissions.incluir && (
                <Route path="/clientUsers/new" element={<NewClientUser />} />
              )}
              {clientUsersPermissions.alterar && (
                <Route path="/clientUsers/:id" element={<EditClientUser />} />
              )}
            </>
          )}

          {internalUsersPermissions?.vizualizar && (
            <>
              <Route path="/internalUsers" element={<InternalUsers />} />

              {internalUsersPermissions.incluir && (
                <Route path="/internalUsers/new" element={<NewInternalUser />} />
              )}

              {internalUsersPermissions.alterar && (
                <Route path="/internalUsers/:id" element={<EditInternalUser />} />
              )}
            </>
          )}

          {partnersPermissions && partnersPermissions.vizualizar && (
            <>
              <Route path="/partners" element={<Partners />} />
              {partnersPermissions.alterar && (
                <Route path="/partners/integrator/:integratorId" element={<Integrator />} />
              )}
            </>
          )}

          {(companyPermissions && companyPermissions.vizualizar) && (
            <>
              <Route path="/company" element={<Company />} />
              {companySettingsPermissions && companySettingsPermissions.vizualizar && (
                <>
                  <Route path="/company/settings" element={<Settings />} />
                </>
              )}
              <Route path="/company/branches" element={<Branches />} />
              <Route path="/company/branches/new" element={<NewBranch />} />
              <Route path="/company/financial" element={<FinancialList />} />
              {canAccessFinancial && (
                <>
                  <Route path="/company/financial/new/:serviceType" element={<FinancialForm />} />
                  <Route path="/company/financial/edit/:codCondCom" element={<FinancialForm />} />
                  <Route path="/company/financial/installments/:codCondCom" element={<FinancialInstallments />} />
                </>
              )}
            </>
          )}

          {(companyPermissions && companyPermissions.vizualizar) && (
            <>
              <Route path="/company" element={<Company />} />
              {companySettingsPermissions && companySettingsPermissions.vizualizar && (
                <Route path="/company/settings" element={<Settings />} />
              )}
              <Route path="/company/branches" element={<Branches />} />
              <Route path="/company/branches/new" element={<NewBranch />} />
            </>
          )}

          <Route path="/linesOperatorsExceptions/:id" element={<LinesOperatorsExceptions />} />
          <Route path="/linesOperatorsBias/:id" element={<LinesOperatorsBias />} />

          {unlockCompaniesPermissions?.vizualizar && (
            <Route path="/unlockingCompanies" element={<UnlockingCompanies />} />
          )}

          {workplacesPermissions && workplacesPermissions.vizualizar && (
            <>
              <Route path="/workplaces" element={<Workplaces />} />
              {workplacesPermissions.incluir && (
                <Route path="/workplaces/new" element={<NewWorkplace />} />
              )}
              {workplacesPermissions.alterar && (
                <Route path="/workplaces/:workplaceId" element={<EditWorkplace />} />
              )}
            </>
          )}

          {operatorsPermissions?.vizualizar && (
            <Route path="/operators" element={<Operators />} />
          )}

          {vtItensPermissions?.vizualizar && (
            <>
              <Route path="/vtItens" element={<VtItens />} />
              <Route path="/integration" element={<Integration />} />
              <Route path="/biasTags" element={<BiasTags />} />
            </>
          )}

          {vtItensRelationsPermissions?.vizualizar && (
            <Route path="/vtItens/relations" element={<Relations />} />
          )}

          {linesPermissions?.vizualizar && (
            <Route path="/transportLines" element={<TransportLines />} />
          )}

          {affreightmentsLinesPermissions && affreightmentsLinesPermissions.vizualizar && (
            <>
              <Route path="/lines" element={<Lines />} />
              {affreightmentsLinesPermissions.incluir && (
                <Route path="/lines/new" element={<NewLine />} />
              )}
              {affreightmentsLinesPermissions.alterar && (
                <Route path="/lines/edit/:lineId" element={<EditLine />} />
              )}
              <Route path="/lines/map" element={<LineMap />} />

              {(affreightmentsLinesPermissions.incluir || affreightmentsLinesPermissions.alterar) && (
                <Route path="/lines/shape" element={<ShapePost />} />
              )}
            </>
          )}

          {affreightmentsVehiclesPermissions && affreightmentsVehiclesPermissions.vizualizar && (
            <>
              <Route path="/vehicles" element={<Vehicles />} />
              {affreightmentsVehiclesPermissions.incluir && (
                <Route path="/vehicles/new" element={<NewVehicle />} />
              )}
              {affreightmentsVehiclesPermissions.alterar && (
                <Route path="/vehicles/:vehicleId" element={<EditVehicle />} />
              )}
            </>
          )}

          {affreightmentsPassengersPermissions && affreightmentsPassengersPermissions.vizualizar && (
            <Route path="/passengers" element={<Passengers />} />
          )}

          {routingPermissions && routingPermissions.vizualizar && (
            <>
              {routingPermissions.incluir && (
                <Route path="/consults/new" element={<NewConsult />} />
              )}
              <Route path="/consults/search" element={<SearchConsult />} />
              <Route path="/consults/result/:consultCode" element={<ConsultResult />} />
            </>
          )}
          {(routingFilePermissions && routingFilePermissions.vizualizar && routingFilePermissions.incluir) && (
            <Route path="/consults/file" element={<ImportConsults />} />
          )}

          {routingFilesPermissions && routingFilesPermissions.vizualizar && (
            <>
              <Route path="/audit/files" element={<AuditFiles />} />
              <Route path="/audit/files/:fileId" element={<FileConsults />} />
            </>
          )}
          <Route path="/audit/consults" element={<AuditConsults />} />

          {reclamationsPermissions && reclamationsPermissions.vizualizar && (
            <>
              <Route path="/reclamations" element={<Reclamations />} />
              <Route path="/reclamations/dashboard" element={<ReclamationsDashboard />} />
              <Route path="/reclamations/consult/:consultCode" element={<Consult />} />
              <Route path="/periodReclamations" element={<ReclamationsAtPeriod />} />
              <Route path="/customersRequests" element={<CustomersRequests />} />
            </>
          )}

          <Route path="/unlockConsults" element={<UnlockConsults />} />
          <Route path="/reprocessConsults" element={<ReprocessConsults />} />
          <Route path="/abateConsults" element={<AbateConsults />} />

          {affreightmentsDashboardPermissions && affreightmentsDashboardPermissions.vizualizar && (
            <>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/lineMap" element={<LineMap />} />
            </>
          )}

          {triangulationPermissions && triangulationPermissions.vizualizar && (
            <Route path="/triangulation" element={<Triangulation />} />
          )}

          {canAccessIago && (
            <Route path="/iago" element={<IagoLayout />}>
              <Route index element={<IagoOverview />} />
              <Route path="contacts" element={<IagoContacts />} />
              <Route path="contacts/:id" element={<IagoContactDetail />} />
              <Route path="parked" element={<IagoParked />} />
              <Route path="escalated" element={<IagoEscalated />} />
              <Route path="leads" element={<IagoLeads />} />
              <Route path="imports" element={<IagoImports />} />
              <Route path="companies" element={<IagoCompanies />} />
              <Route path="companies/:id" element={<IagoCompanyDetail />} />
            </Route>
          )}

          {autImplantationPermissions && autImplantationPermissions.vizualizar && (
            <Route path="/autImplantation" element={<AutImplantation />} />
          )}

          <Route path="/financial/omie-installments" element={<OmieInstallments />} />
          <Route path="/itens-relations" element={<ItensRelations />} />
          <Route path="/importVtRegistrationData" element={<ImportVtRegistrationDataList />} />
          <Route path="/importVtRegistrationData/new" element={<ImportVtRegistrationDataNew />} />
          <Route path="/importVtRegistrationData/:codArq" element={<ImportVtRegistrationDataDetails />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}
